/**
 * Durable key/value for NW.js (JSON file under nw.App.dataPath).
 * Browser Vite falls back to localStorage.
 * One-time migrate: copy known keys from localStorage into the file if missing.
 */
const SETTINGS_FILE_NAME = 'zmg_app_settings.json';

/** Keys that should survive Chromium clearing localStorage */
const MIGRATE_KEYS = [
    'MY_SETTINGS_LISTENING_STORES_DEVICE',
    'PRINT_AFTER_ACCEPT',
    'PRINT_ORDER_ON_PLACED',
    'COPIES_NUMBER',
    'FONT_SIZE_REM',
    'FONT_WEIGHT',
    'currentPrinter',
    'PRINT_ON_PLACED_SENT',
];

type Store = Record<string, string>;

const isNw = typeof nw !== 'undefined';
let memory: Store | null = null;

function getSettingsFilePath(): string {
    const path = require('path');
    return path.join(nw.App.dataPath, SETTINGS_FILE_NAME);
}

function readFile(): Store {
    try {
        const fs = require('fs');
        const filePath = getSettingsFilePath();
        if (!fs.existsSync(filePath)) return {};
        const raw = fs.readFileSync(filePath, 'utf8');
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        if (typeof parsed !== 'object' || !parsed || Array.isArray(parsed)) return {};
        const store: Store = {};
        for (const [key, value] of Object.entries(parsed)) {
            if (typeof value === 'string') store[key] = value;
        }
        return store;
    } catch (error) {
        console.warn('[settings] NW file load failed:', error);
        return {};
    }
}

function writeFile(store: Store): void {
    try {
        const fs = require('fs');
        fs.writeFileSync(getSettingsFilePath(), JSON.stringify(store), 'utf8');
    } catch (error) {
        console.warn('[settings] NW file save failed:', error);
    }
}

function ensureLoaded(): void {
    if (memory) return;
    if (!isNw) {
        memory = {};
        return;
    }
    memory = readFile();
    let migrated = false;
    for (const key of MIGRATE_KEYS) {
        if (Object.prototype.hasOwnProperty.call(memory, key)) continue;
        if (!localStorage.hasOwnProperty(key)) continue;
        const value = localStorage.getItem(key);
        if (value == null) continue;
        memory[key] = value;
        migrated = true;
    }
    if (migrated) {
        writeFile(memory);
    }
}

export function durableHas(key: string): boolean {
    if (!isNw) return localStorage.hasOwnProperty(key);
    ensureLoaded();
    return Object.prototype.hasOwnProperty.call(memory, key);
}

export function durableGetItem(key: string): string | null {
    if (!isNw) return localStorage.getItem(key);
    ensureLoaded();
    return memory![key] ?? null;
}

export function durableSetItem(key: string, value: string): void {
    if (!isNw) {
        localStorage.setItem(key, value);
        return;
    }
    ensureLoaded();
    memory![key] = value;
    writeFile(memory!);
}

export function durableRemoveItem(key: string): void {
    if (!isNw) {
        localStorage.removeItem(key);
        return;
    }
    ensureLoaded();
    delete memory![key];
    writeFile(memory!);
}
